/**
 * EventsDashboard — admin home for Event Pre-Orders (/events).
 * Three tabs: Active (draft/open), History (closed/archived past events), and
 * the reusable Menus library. Live order counts, lifecycle actions, and a
 * QR/link share sheet.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  subscribeEvents, subscribeAllOrders, subscribeMenus, createEvent, setEventStatus, deleteEvent,
} from './eventsCloud';
import {
  STATUS_META, timeAgo, formatDateTime, ordersRevenue, formatMoney, buildActivityFeed, toMs,
} from './eventUtils';
import ShareModal from './components/ShareModal';
import ConfirmModal from './components/ConfirmModal';
import MenuLibrary from './components/MenuLibrary';
import ActivityFeed from './components/ActivityFeed';
import { t } from './strings';
import './Events.css';

const ACTIVE_STATUSES = ['draft', 'open'];

export default function EventsDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [events, setEvents] = useState(null);
  const [orders, setOrders] = useState([]);
  const [menus, setMenus] = useState(null);
  const [tab, setTab] = useState('active');
  const [search, setSearch] = useState('');
  const [shareEv, setShareEv] = useState(null);
  const [deleteEvent_, setDeleteEvent_] = useState(null);
  const [toast, setToast] = useState('');
  const [err, setErr] = useState('');

  const autoClosedRef = useRef(new Set());

  useEffect(() => {
    const u1 = subscribeEvents(setEvents, (e) => setErr(e.message));
    const u2 = subscribeAllOrders(setOrders, () => {});
    const u3 = subscribeMenus(setMenus, () => setMenus([]));
    return () => { u1(); u2(); u3(); };
  }, []);

  // Automated deadline-close: flip any open event past its deadline to closed
  // (logged to history). Best-effort — fires while a manager dashboard is open.
  useEffect(() => {
    if (!events) return;
    const now = Date.now();
    for (const ev of events) {
      const deadlineMs = toMs(ev.deadline);
      if (ev.status === 'open' && deadlineMs && deadlineMs < now && !autoClosedRef.current.has(ev.id)) {
        autoClosedRef.current.add(ev.id);
        setEventStatus(ev.id, 'closed', { displayName: 'Auto · deadline' })
          .catch(() => autoClosedRef.current.delete(ev.id));
      }
    }
  }, [events]);

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast((m) => (m === msg ? '' : m)), 2200); };

  const feed = useMemo(() => buildActivityFeed(events || [], orders), [events, orders]);

  const byEvent = useMemo(() => {
    const m = {};
    for (const o of orders) (m[o.eventId] ||= []).push(o);
    return m;
  }, [orders]);

  const active = useMemo(() => (events || []).filter((e) => ACTIVE_STATUSES.includes(e.status)), [events]);
  const history = useMemo(() => (events || []).filter((e) => !ACTIVE_STATUSES.includes(e.status)), [events]);

  const filtered = useMemo(() => {
    const list = tab === 'history' ? history : active;
    const q = search.trim().toLowerCase();
    return q ? list.filter((e) => (e.title || '').toLowerCase().includes(q)) : list;
  }, [tab, active, history, search]);

  const stats = useMemo(() => ({
    open: (events || []).filter((e) => e.status === 'open').length,
    orders: orders.length,
    events: (events || []).length,
  }), [events, orders]);

  const setStatus = async (ev, status, msg) => {
    await setEventStatus(ev.id, status, user);
    if (msg) flash(msg);
  };

  const duplicate = async (ev) => {
    // eslint-disable-next-line no-unused-vars
    const { id, createdAt, updatedAt, createdBy, createdByName, history: _h, ...rest } = ev;
    const newId = await createEvent({ ...rest, title: `${ev.title || 'Untitled'} (copy)`, status: 'draft' }, user);
    navigate(`/events/${newId}/edit`);
  };

  const confirmDelete = async () => {
    const ev = deleteEvent_;
    setDeleteEvent_(null);
    if (!ev) return;
    try { await deleteEvent(ev.id); flash('Event deleted'); } catch (e) { setErr(e.message); }
  };

  const renderCard = (ev) => {
    const meta = STATUS_META[ev.status] || STATUS_META.draft;
    const evOrders = byEvent[ev.id] || [];
    const n = evOrders.length;
    const rev = ordersRevenue(evOrders);
    const isOpen = ev.status === 'open';
    const isArchived = ev.status === 'archived';
    return (
      <div key={ev.id} className="ev__card" style={{ '--card-accent': ev.bannerColor || '#f59e0b' }}>
        <div className="ev__card-top">
          <span className="ev__chip" style={{ color: meta.color, background: `${meta.color}22` }}>
            <span className="ev__chip-dot" style={{ background: meta.color }} />{meta.label}
          </span>
          <span className="ev__card-count" title={`${n} orders`}>👥 {n}{rev > 0 ? ` · ${formatMoney(rev)}` : ''}</span>
        </div>

        <h3 className="ev__card-title">{ev.title || 'Untitled event'}</h3>
        {ev.description && <p className="ev__card-desc">{ev.description}</p>}

        <div className="ev__card-meta">
          <span>🍽 {(ev.menuItems || []).length}{ev.menuLink ? ' + link' : ''}</span>
          {ev.createdAt ? <span>· {timeAgo(ev.createdAt)}</span> : null}
          {ev.deadline ? <span>· ⌛ {formatDateTime(ev.deadline)}</span> : null}
        </div>

        <div className="ev__card-actions">
          {tab === 'active' ? (
            <button className={`ev__btn ev__btn--sm ev__btn--wide ${isOpen ? 'ev__btn--danger' : 'ev__btn--go'}`}
              onClick={() => setStatus(ev, isOpen ? 'closed' : 'open', isOpen ? t('eventClosed') : t('eventOpened'))}>
              {isOpen ? `⏸ ${t('close')}` : `⚡ ${t('open')}`}
            </button>
          ) : (
            <button className="ev__btn ev__btn--sm ev__btn--wide ev__btn--go"
              onClick={() => setStatus(ev, 'open', t('eventOpened'))}>
              ⚡ {t('reopen')}
            </button>
          )}
          <button className="ev__btn ev__btn--sm" onClick={() => setShareEv(ev)}>🔗 {t('share')}</button>
          <button className="ev__btn ev__btn--sm" onClick={() => navigate(`/events/${ev.id}/responses`)}>
            📥 {t('responses')}{n ? ` (${n})` : ''}
          </button>
          <button className="ev__btn ev__btn--sm" onClick={() => navigate(`/events/${ev.id}/edit`)}>✎ {t('edit')}</button>
          <span className="ev__card-actions-spacer" />
          {tab === 'history' && (
            isArchived
              ? <button className="ev__iconbtn" title={t('restore')} onClick={() => setStatus(ev, 'draft', t('restore'))}>♻️</button>
              : <button className="ev__iconbtn" title={t('archive')} onClick={() => setStatus(ev, 'archived', 'Archived')}>🗄️</button>
          )}
          <button className="ev__iconbtn" title={t('duplicate')} onClick={() => duplicate(ev)}>⧉</button>
          <button className="ev__iconbtn ev__iconbtn--danger" title={t('del')} onClick={() => setDeleteEvent_(ev)}>🗑</button>
        </div>
      </div>
    );
  };

  const loading = events === null;

  return (
    <div className="ev">
      <div className="ev__inner">
        <header className="ev__hero">
          <div>
            <h1 className="ev__title"><span className="ev__title-grad">{t('dashboardTitle')}</span></h1>
            <p className="ev__sub">{t('dashboardSubtitle')}</p>
          </div>
          <button className="ev__btn ev__btn--primary" onClick={() => navigate('/events/new')}>
            + {t('newEvent')}
          </button>
        </header>

        {/* Stats strip */}
        <div className="ev-stats">
          <div className="ev-stat"><span className="ev-stat__num">{stats.open}</span><span className="ev-stat__lbl">{t('statOpen')}</span></div>
          <div className="ev-stat"><span className="ev-stat__num">{stats.orders}</span><span className="ev-stat__lbl">{t('statOrders')}</span></div>
          <div className="ev-stat"><span className="ev-stat__num">{stats.events}</span><span className="ev-stat__lbl">{t('statEvents')}</span></div>
        </div>

        {/* Tabs */}
        <div className="ev-tabs">
          <button className={`ev-tab${tab === 'active' ? ' is-on' : ''}`} onClick={() => setTab('active')}>
            {t('tabActive')} <span className="ev-tab__badge">{active.length}</span>
          </button>
          <button className={`ev-tab${tab === 'history' ? ' is-on' : ''}`} onClick={() => setTab('history')}>
            {t('tabHistory')} <span className="ev-tab__badge">{history.length}</span>
          </button>
          <button className={`ev-tab${tab === 'menus' ? ' is-on' : ''}`} onClick={() => setTab('menus')}>
            {t('tabMenus')} <span className="ev-tab__badge">{menus ? menus.length : 0}</span>
          </button>
          <button className={`ev-tab${tab === 'activity' ? ' is-on' : ''}`} onClick={() => setTab('activity')}>
            {t('tabActivity')} <span className="ev-tab__badge">{feed.length}</span>
          </button>
        </div>

        {err && <div className="ev__error">{err}</div>}

        {tab === 'activity' ? (
          <ActivityFeed feed={feed} events={events || []} />
        ) : tab === 'menus' ? (
          <MenuLibrary menus={menus} user={user} onToast={flash} />
        ) : (
          <>
            {(tab === 'history' ? history : active).length > 0 && (
              <input className="ev-search" placeholder={t('searchPlaceholder')}
                value={search} onChange={(e) => setSearch(e.target.value)} />
            )}
            {loading ? (
              <div className="ev__grid">{[0, 1, 2].map((i) => <div key={i} className="ev__card ev__card--skeleton" />)}</div>
            ) : filtered.length === 0 ? (
              <div className="ev__empty">
                <div className="ev__empty-icon">{tab === 'history' ? '🗂️' : '📝'}</div>
                <h3>{search ? t('noSearch') : tab === 'history' ? t('noHistory') : t('noEvents')}</h3>
                {tab === 'active' && !search && (
                  <button className="ev__btn ev__btn--primary" onClick={() => navigate('/events/new')}>+ {t('newEvent')}</button>
                )}
              </div>
            ) : (
              <div className="ev__grid">{filtered.map(renderCard)}</div>
            )}
          </>
        )}
      </div>

      <ShareModal open={!!shareEv} onClose={() => setShareEv(null)} eventId={shareEv?.id} title={shareEv?.title} />
      <ConfirmModal
        open={!!deleteEvent_}
        onClose={() => setDeleteEvent_(null)}
        onConfirm={confirmDelete}
        title="Delete event?"
        message={t('confirmDelete')}
        confirmLabel="Delete"
      />
      {toast && <div className="ev-toast">{toast}</div>}
    </div>
  );
}
